/**
 * A button and modal dialog for deleting a scratchpad.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {useGql, type GqlFetchFn} from "@khan/data-gql";

const DeleteModalState = {
    DELETE_PROMPT: "prompt" as const,
    DELETE_IN_PROGRESS: "inprogress" as const,
    DELETE_ERROR: "error" as const,
} as const;

type Copy = {
    title: string;
    body: string;
    showSpinner: boolean;
};

type Props = {
    // Handle deleting the current program
    // Should return a promise which resolves upon success/failure
    deleteProgram: () => Promise<unknown>;
    onCancel: () => void;
};

type State = {
    deleteModalState: (typeof DeleteModalState)[keyof typeof DeleteModalState];
};

class DeleteModal extends React.Component<Props, State> {
    state: State = {
        deleteModalState: DeleteModalState.DELETE_PROMPT,
    };

    render(): React.ReactNode {
        const state = this.state.deleteModalState;

        const handleDelete = () => {
            this.setState({
                deleteModalState: DeleteModalState.DELETE_IN_PROGRESS,
            });

            this.props.deleteProgram().catch(() =>
                this.setState({
                    deleteModalState: DeleteModalState.DELETE_ERROR,
                }),
            );
        };
        const footer = state === DeleteModalState.DELETE_PROMPT && (
            <View style={styles.modalFooter}>
                <Button kind="tertiary" onClick={this.props.onCancel}>
                    {t`Cancel`}
                </Button>
                <Strut size={spacing.medium_16} />
                <Button
                    actionType="destructive"
                    onClick={() => {
                        handleDelete();
                    }}
                >
                    {t`Delete`}
                </Button>
            </View>
        );

        const DeleteModalText: Record<
            (typeof DeleteModalState)[keyof typeof DeleteModalState],
            Copy
        > = {
            [DeleteModalState.DELETE_PROMPT]: {
                title: t`Delete this project?`,
                body: t`Are you sure you want to delete this project? This can't be undone.`,
                showSpinner: false,
            },

            [DeleteModalState.DELETE_IN_PROGRESS]: {
                title: t`Deleting...`,
                body: t`Deleting project. You'll be redirected when it's done.`,
                showSpinner: true,
            },

            [DeleteModalState.DELETE_ERROR]: {
                title: t`Deletion Failed.`,
                body: t`Something went wrong and we couldn't delete the project. Please try again!`,
                showSpinner: false,
            },
        };

        return (
            <OnePaneDialog
                closeButtonVisible={
                    state !== DeleteModalState.DELETE_IN_PROGRESS
                }
                title={DeleteModalText[state].title}
                content={
                    <View>
                        <Body tag="p">{DeleteModalText[state].body}</Body>
                        {DeleteModalText[state].showSpinner && (
                            <CircularSpinner size="medium" />
                        )}
                    </View>
                }
                footer={footer}
                style={styles.modal}
            />
        );
    }
}

type DeleteButtonProps = {
    // Handles deleting the current scratchpad
    // Should return a promise which resolves upon success/failure
    deleteProgram: (gqlFetch: GqlFetchFn) => Promise<void>;
};

function ScratchpadDeleteButton({
    deleteProgram,
}: DeleteButtonProps): React.ReactElement {
    const gqlFetch = useGql();

    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <DeleteModal
                    deleteProgram={() => deleteProgram(gqlFetch)}
                    onCancel={closeModal}
                />
            )}
        >
            {({openModal}) => (
                <Button kind="secondary" onClick={openModal}>
                    {t`Delete`}
                </Button>
            )}
        </ModalLauncher>
    );
}

const styles = StyleSheet.create({
    modal: {
        maxHeight: 300,
    },
    modalFooter: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
});

export default ScratchpadDeleteButton;
